import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faStar,
  faSpinner,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import flashcardService, { isQuotaError } from '../../services/flashcardService';
import UpgradePrompt from './UpgradePrompt';
import './Flashcards.css';

const PAGE_SIZE = 5;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

const StaticStars = ({ value }) => (
  <span className="fc-stars">
    {Array.from({ length: 10 }).map((_, i) => (
      <FontAwesomeIcon
        key={i}
        icon={faStar}
        className={i < (value || 0) ? '' : 'fc-star-empty'}
      />
    ))}
  </span>
);

const ReviewModal = ({ deck, entitlement, onClose, onSaved }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [hasMine, setHasMine] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [quotaBlocked, setQuotaBlocked] = useState(false);

  const loadReviews = useCallback(
    (pageNumber) => {
      setLoading(true);
      flashcardService
        .getReviews(deck.id, { pageNumber, pageSize: PAGE_SIZE })
        .then((res) => {
          const items = res?.items || [];
          setReviews(items);
          setTotalPages(res?.totalPages || 1);
          // Prefill from my existing review (present on the first page as isMine).
          if (pageNumber === 1) {
            const mine = items.find((r) => r.isMine);
            if (mine) {
              setRating(mine.rating);
              setReviewText(mine.reviewText || '');
              setHasMine(true);
            }
          }
        })
        .catch(() => setError('Could not load reviews.'))
        .finally(() => setLoading(false));
    },
    [deck.id]
  );

  useEffect(() => {
    loadReviews(page);
  }, [page, loadReviews]);

  const handleSubmit = async () => {
    if (rating < 1) {
      setError('Pick a rating from 1 to 10.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await flashcardService.upsertReview(deck.id, {
        rating,
        reviewText: reviewText.trim(),
      });
      onSaved();
    } catch (err) {
      if (isQuotaError(err)) setQuotaBlocked(true);
      else setError('Could not submit your review. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      await flashcardService.deleteReview(deck.id);
      onSaved();
    } catch (err) {
      setError('Could not remove your review.');
    } finally {
      setSaving(false);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div className="fc-modal-overlay" onClick={onClose}>
      <div
        className="fc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="fc-modal-header">
          <h2>Review {deck.name}</h2>
          <button type="button" className="fc-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {quotaBlocked ? (
          <div className="fc-modal-body">
            <UpgradePrompt
              variant="review"
              requiredPlans={entitlement?.requiredPlans}
              onDismiss={onClose}
              dismissLabel="Not now"
            />
          </div>
        ) : (
          <>
            <div className="fc-modal-body">
              {error && <div className="fc-error-banner">{error}</div>}

              <div className="fc-field">
                <span className="fc-label">Your Rating</span>
                <div className="fc-star-input" onMouseLeave={() => setHoverRating(0)}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`fc-star-btn ${
                        i < displayRating ? 'fc-star-active' : ''
                      }`}
                      onMouseEnter={() => setHoverRating(i + 1)}
                      onClick={() => setRating(i + 1)}
                      aria-label={`${i + 1} out of 10`}
                    >
                      <FontAwesomeIcon icon={faStar} />
                    </button>
                  ))}
                  <span className="fc-star-score">{displayRating}/10</span>
                </div>
              </div>

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-review-text">
                  Your Review (optional)
                </label>
                <textarea
                  id="fc-review-text"
                  className="fc-textarea"
                  maxLength={2000}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Share your thoughts about this deck…"
                />
              </div>

              <div className="fc-stats-section">
                <h3>Recent Reviews</h3>
                {loading ? (
                  <div className="fc-state">
                    <FontAwesomeIcon icon={faSpinner} className="fc-spin fc-state-icon" />
                  </div>
                ) : reviews.length === 0 ? (
                  <p className="fc-no-rating">No reviews yet. Be the first!</p>
                ) : (
                  <>
                    <div className="fc-reviews-list">
                      {reviews.map((r) => (
                        <div key={r.id} className="fc-review-item">
                          <div className="fc-review-item-head">
                            <span className="fc-review-author">
                              {r.userDisplayName}
                              {r.isMine ? ' (you)' : ''}
                            </span>
                            <span className="fc-review-date">
                              {formatDate(r.updatedAt)}
                            </span>
                          </div>
                          <StaticStars value={r.rating} />
                          {r.reviewText && (
                            <p className="fc-review-text">{r.reviewText}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="fc-pagination">
                        <button
                          type="button"
                          className="fc-btn fc-btn-ghost fc-btn-sm"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => p - 1)}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span className="fc-page-label">
                          {page} / {totalPages}
                        </span>
                        <button
                          type="button"
                          className="fc-btn fc-btn-ghost fc-btn-sm"
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="fc-modal-footer">
              {hasMine && (
                <button
                  type="button"
                  className="fc-btn fc-btn-danger"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              )}
              <button
                type="button"
                className="fc-btn fc-btn-ghost"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fc-btn fc-btn-primary"
                onClick={handleSubmit}
                disabled={saving || rating < 1}
              >
                {saving && <FontAwesomeIcon icon={faSpinner} className="fc-spin" />}
                {hasMine ? 'Update Review' : 'Submit Review'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReviewModal;
